import { useState, useCallback, useEffect } from "react";

const SESSION_LOCATION_KEY = "eatzy_session_location";
const SYNC_EVENT = "eatzy-session-location-updated";

interface SessionLocation {
  name: string;
  latitude?: number;
  longitude?: number;
  /** Eatzy saved-address ID, when the user picked a saved address (not an ad-hoc Google Place). */
  addressId?: number;
}

function getStoredLocation(): SessionLocation | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(SESSION_LOCATION_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (_e) {
    // ignore
  }
  return null;
}

function storeLocation(loc: SessionLocation) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_LOCATION_KEY, JSON.stringify(loc));
  } catch (_e) {
    // ignore
  }
}

export function clearSessionLocation() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SESSION_LOCATION_KEY);
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  } catch (_e) {
    // ignore
  }
}

export function useSessionLocation() {
  const [selectedLocationName, setSelectedLocationNameState] = useState<string>(
    () => getStoredLocation()?.name ?? ""
  );
  const [customLatitude, setCustomLatitudeState] = useState<number | undefined>(
    () => getStoredLocation()?.latitude
  );
  const [customLongitude, setCustomLongitudeState] = useState<
    number | undefined
  >(() => getStoredLocation()?.longitude);
  const [selectedAddressId, setSelectedAddressIdState] = useState<
    number | undefined
  >(() => getStoredLocation()?.addressId);

  useEffect(() => {
    const handleSync = () => {
      const loc = getStoredLocation();
      setSelectedLocationNameState(loc?.name ?? "");
      setCustomLatitudeState(loc?.latitude);
      setCustomLongitudeState(loc?.longitude);
      setSelectedAddressIdState(loc?.addressId);
    };
    window.addEventListener(SYNC_EVENT, handleSync);
    return () => window.removeEventListener(SYNC_EVENT, handleSync);
  }, []);

  const setSessionLocation = useCallback(
    (name: string, lat?: number, lng?: number, addressId?: number) => {
      setSelectedLocationNameState(name);
      setCustomLatitudeState(lat);
      setCustomLongitudeState(lng);
      setSelectedAddressIdState(addressId);
      if (name) {
        storeLocation({ name, latitude: lat, longitude: lng, addressId });
      } else {
        clearSessionLocation();
        return;
      }
      window.dispatchEvent(new CustomEvent(SYNC_EVENT));
    },
    []
  );

  return {
    selectedLocationName,
    customLatitude,
    customLongitude,
    selectedAddressId,
    setSessionLocation,
  };
}
